import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { Client } from '@app/core/types/client';
import { BrandCodes } from '@app/core/types/brand';
import {
  DOCUMENT_STATUS,
  ID_VERIFICATION_STATUS,
  SLOT_CONFIGS,
  SLOT_KEYS,
  VerificationDetail,
  parseDocumentStatus,
} from '@app/core/types/id-verification';

@Component({
  selector: 'itfg-identity-verification',
  templateUrl: './identity-verification.component.html',
  styleUrl: './identity-verification.component.scss',
  standalone: false,
})
export class IdentityVerificationComponent implements OnChanges, OnDestroy {
  @Input() client: Client;

  verifications: any[] = [];
  currentIndex = 0;
  currentDetail: any | null = null;
  loadingList = false;
  loadingDetail = false;
  expanded = false;
  selectedEventId: string | number | null = null;

  /** Shared with the sibling documents component. */
  readonly detail$ = new BehaviorSubject<VerificationDetail | null>(null); // TODO: move to a service!

  private _unsubscribe = new Subject<void>();

  // ── Display config ────────────────────────────────────────────────

  readonly STATUS_ICON: Record<ID_VERIFICATION_STATUS, string> = {
    [ID_VERIFICATION_STATUS.IN_PROGRESS]: 'hourglass_empty',
    [ID_VERIFICATION_STATUS.COMPLETED]: 'check_circle',
    [ID_VERIFICATION_STATUS.MANUAL]: 'person',
  };

  readonly STATUS_CLASS: Record<ID_VERIFICATION_STATUS, string> = {
    [ID_VERIFICATION_STATUS.IN_PROGRESS]: 'pending',
    [ID_VERIFICATION_STATUS.COMPLETED]: 'approved',
    [ID_VERIFICATION_STATUS.MANUAL]: 'expired',
  };

  readonly PROC_COLOR: Record<string, string> = {
    SHUFTI: '#6a1b9a',
    OPERATOR: '#e65100',
    INTERNAL: '#546e7a',
    EXTERNAL: '#4527a0',
    SYSTEM: '#90a4ae',
  };

  /**
   * Each entry describes one doc-status dot shown in the collapsed row.
   * Derived from SLOT_CONFIGS so adding a new slot automatically appears here.
   */
  readonly DOC_DOTS = SLOT_KEYS.map(key => ({
    label: SLOT_CONFIGS[key].label,
    statusKey: SLOT_CONFIGS[key].statusKey,
  }));

  // ── Stats ─────────────────────────────────────────────────────────

  get total(): number {
    return this.verifications.length;
  }

  get completed(): number {
    return this.verifications.filter(v => v.status === ID_VERIFICATION_STATUS.COMPLETED).length;
  }

  get manual(): number {
    return this.verifications.filter(v => v.status === ID_VERIFICATION_STATUS.MANUAL).length;
  }

  get inProgress(): number {
    return this.verifications.filter(v => v.status === ID_VERIFICATION_STATUS.IN_PROGRESS).length;
  }

  get last24h(): number {
    const cutoff = Date.now() - 86_400_000;
    return this.verifications.filter(v => new Date(v.startedAt).getTime() > cutoff).length;
  }

  get eventsDesc(): any[] {
    return [...(this.currentDetail?.events ?? [])].reverse();
  }

  constructor(
    private service: IdentityReportsService,
    private snackBar: MatSnackBar,
  ) { }

  // ── Lifecycle ─────────────────────────────────────────────────────

  ngOnChanges(): void {
    if (this.client?.id && this.client?.civilId) {
      this.loadList();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  // ── Data ──────────────────────────────────────────────────────────

  loadList(): void {
    this.loadingList = true;
    this.verifications = [];
    this.currentIndex = 0;
    // Hard-clear only on full client reset
    this.currentDetail = null;
    this.detail$.next(null);

    this.service
      .getIdentityVerificationList(this.client.civilId, this.client.brand.code as BrandCodes)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (data: any[]) => {
          this.verifications = data;
          if (data.length) {
            this.loadDetail(data[0].id);
          }
        },
        error: e => console.error(e),
        complete: () => {
          this.loadingList = false;
        },
      });
  }

  loadDetail(id: number): void {
    this.loadingDetail = true;
    // Keep stale currentDetail and detail$ as-is while loading —
    // the row dims via iv-row--loading, no blanking flash.

    this.service
      .getIdentityVerificationReport(id)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (data: any) => {
          this.currentDetail = data;
          this.detail$.next(data);
        },
        error: e => console.error(e),
        complete: () => {
          this.loadingDetail = false;
        },
      });
  }

  navigate(direction: -1 | 1): void {
    const next = this.currentIndex + direction;
    if (next < 0 || next >= this.verifications.length) {
      return;
    }
    this.currentIndex = next;
    this.loadDetail(this.verifications[next].id);
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  toggleEvent(id: string | number): void {
    this.selectedEventId = this.selectedEventId === id ? null : id;
  }

  /** Flattens ev.data into [key, value] pairs for display. */
  eventDataEntries(ev: any): [string, string][] {
    if (!ev?.data || typeof ev.data !== 'object') {
      return [];
    }

    return Object.entries(ev.data).map(([k, v]) => {
      const display = v === null || v === undefined ? '—' : String(v);
      return [k, display];
    });
  }

  // ── Actions ───────────────────────────────────────────────────────

  startVerification(): void {
    this.service
      .startIdentityVerification(this.client.civilId, this.client.brand.code as BrandCodes)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: () => this.loadList(),
        error: e => console.error(e),
      });

    this.snackBar.open('Verification started', 'OK', { duration: 2000 });
  }

  decide(action: 'approve' | 'reject'): void {
    const label = action === 'approve' ? 'Approved' : 'Rejected';
    this.snackBar.open(`${label} #${this.currentDetail?.id}`, 'OK', { duration: 2000 });
  }

  // ── Helpers ───────────────────────────────────────────────────────

  statusClass(status: string): string {
    return this.STATUS_CLASS[status as ID_VERIFICATION_STATUS] ?? 'expired';
  }

  /**
   * Maps a raw document status string to a CSS dot modifier.
   * Parses the base status first so 'INVALID_PA_KATA' → 'rejected'.
   */
  docDotClass(rawStatus: string): string {
    const base = parseDocumentStatus(rawStatus).base;

    const dotClass: Record<DOCUMENT_STATUS, string> = {
      [DOCUMENT_STATUS.PENDING]: 'pending',
      [DOCUMENT_STATUS.VALID]: 'approved',
      [DOCUMENT_STATUS.INVALID]: 'rejected',
      [DOCUMENT_STATUS.PROVIDED]: 'pending',
      [DOCUMENT_STATUS.VALIDATING_SIMPLE]: 'pending',
      [DOCUMENT_STATUS.VALIDATING_COMPLEX]: 'pending',
    };

    return dotClass[base] ?? 'expired';
  }

  procColor(processor: string): string {
    return this.PROC_COLOR[processor] ?? '#546e7a';
  }

  isFailEvent(type: string): boolean {
    return /FAIL|REJECT|ERROR/.test(type);
  }

  isSuccessEvent(type: string): boolean {
    return /APPROVE|COMPLETE|MATCH/.test(type);
  }

  duration(startedAt: string, completedAt: string | null): string {
    if (!completedAt) {
      return 'ongoing';
    }

    const totalSeconds = Math.round((+new Date(completedAt) - +new Date(startedAt)) / 1000);

    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }

  relTime(startTs: string, eventTs: string): string {
    const totalSeconds = Math.round((+new Date(eventTs) - +new Date(startTs)) / 1000);

    if (totalSeconds <= 0) {
      return '';
    }

    if (totalSeconds < 60) {
      return `+${totalSeconds}s`;
    }

    return `+${Math.floor(totalSeconds / 60)}m`;
  }
}